import React from 'react';
import {
  Box,
  Button,
  HStack,
  Text,
  VStack,
  useColorModeValue,
  Icon,
  Flex,
} from '@chakra-ui/react';
import { FaEthereum, FaCoins } from 'react-icons/fa';
import { useChainManager } from '../../chain/hooks/useChainManager';
import { SupportedChain } from '../../constants';

interface ChainSelectorProps {
  onChainChange?: (chain: string) => void;
}

export const ChainSelector: React.FC<ChainSelectorProps> = ({
  onChainChange,
}) => {
  const { currentChain, switchChain } = useChainManager();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const handleChainSwitch = async (chain: SupportedChain) => {
    try {
      await switchChain(chain);
      onChainChange?.(chain);
    } catch (error) {
      console.error('Failed to switch chain:', error);
    }
  };

  const chains = [
    {
      id: 'bnb',
      name: 'BNB Smart Chain',
      icon: FaEthereum,
      color: '#F3BA2F',
      bgGradient: 'linear(to-r, #F3BA2F, #FFD700)',
      borderColor: '#F3BA2F',
    },
    {
      id: 'solana',
      name: 'Solana',
      icon: FaCoins,
      color: '#9945FF',
      bgGradient: 'linear(to-r, #9945FF, #14F195)',
      borderColor: '#9945FF',
    },
  ];

  return (
    <VStack spacing={2} align="stretch">
      <Text
        fontSize="sm"
        fontWeight="semibold"
        color="white"
        textAlign="center"
        textShadow="0 0 8px rgba(255,255,255,0.5)"
        letterSpacing="0.5px"
      >
        Select Blockchain
      </Text>
      <HStack spacing={2} justify="center">
        {chains.map(chain => {
          const isSelected = currentChain === chain.id;
          return (
            <Button
              key={chain.id}
              variant="outline"
              size="sm"
              minW="120px"
              h="40px"
              onClick={() => handleChainSwitch(chain.id as SupportedChain)}
              bg={isSelected ? 'white' : 'rgba(255,255,255,0.05)'}
              borderColor={
                isSelected ? chain.borderColor : 'rgba(255,255,255,0.2)'
              }
              borderWidth={isSelected ? '2px' : '1px'}
              color={isSelected ? chain.color : 'white'}
              fontWeight={isSelected ? 'bold' : 'medium'}
              _hover={{
                bg: isSelected ? 'white' : 'rgba(255,255,255,0.1)',
                borderColor: chain.borderColor,
                transform: 'translateY(-1px)',
                boxShadow: `0 2px 8px ${chain.color}30`,
              }}
              _active={{
                transform: 'translateY(0px)',
              }}
              transition="all 0.15s ease"
              boxShadow={isSelected ? `0 0 12px ${chain.color}40` : 'none'}
              position="relative"
              overflow="hidden"
            >
              <HStack spacing={2}>
                <Icon
                  as={chain.icon}
                  color={isSelected ? chain.color : 'white'}
                  fontSize="16px"
                />
                <Text
                  fontSize="xs"
                  fontWeight={isSelected ? 'bold' : 'medium'}
                  color={isSelected ? chain.color : 'white'}
                >
                  {chain.name}
                </Text>
              </HStack>
              {isSelected && (
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  bgGradient={chain.bgGradient}
                  opacity={0.08}
                  pointerEvents="none"
                />
              )}
            </Button>
          );
        })}
      </HStack>
    </VStack>
  );
};

export default ChainSelector;
